import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { MyProfileViewModel, SessionMenuViewModel, TooltipModel, TooltipModelMaybe } from 'autogen/swagger/all';
import { LogIn, MyProfile as MyProfileTrans } from 'autogen/translation-keys/trans-website-shared';
import InlineButton from 'components/generic/InlineButton';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';
import Icon from 'components/Icon';
import { SessionMenuAnchorItem, SessionMenuItem } from 'components/SessionMenu/components/SessionMenuItem';
import Tooltip from 'components/shared/Tooltip/Tooltip';
import format from 'helpers/translations';
import { useDynamicStorage } from 'hooks/storage';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import useLoginLink from 'hooks/user/useLoginLink';
import loadHtmlModalFunc from 'modules/modal/helpers';
import Default from 'modules/modal/layouts';
import { bindComponents } from 'modules/modalLink';
import { GA4Tracking } from 'modules/tracking/GA4';
import { breakpoint } from 'variables';
import { HeaderItemText } from './shared/styles';

type TooltipType = 'Login' | 'MemberBenefits' | 'Unknown';

const RelativeContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 32px;

    position: relative;

    ${breakpoint.up('768px')} {
        width: 20px;
        height: 20px;
    }

    &:after {
        content: '';
        position: absolute;
        right: 10px;
        top: 6px;
        z-index: 1;
        background-color: #f23939;
        width: 8px;
        height: 8px;
        display: block;
        border-radius: 100%;

        ${breakpoint.up('768px')} {
            right: 0;
            top: 0;
        }
    }

    // used to fix alignment of text
    svg {
        width: 20px;
        height: 20px;
    }
`;

const StyledTooltip = styled(Tooltip)`
    && {
        max-width: 250px;
    }
`;

function getTooltipType(tooltipType: TooltipModel['tooltipType']): TooltipType {
    switch (tooltipType) {
        case 'ClubMatas':
            return 'Login';
        case 'MemberBenefits':
            return 'MemberBenefits';
        default:
            return 'Unknown';
    }
}

function MyProfileLoginModal({ model: { loginRequest } }: { model: MyProfileViewModel }) {
    const { t } = useTranslation();
    const { loginLinkAction } = loginRequest;

    return (
        <SessionMenuItem
            icon={<Icon type="User" size={16} aria-hidden />}
            onClick={async () => {
                const htmlModal = await loadHtmlModalFunc();
                const modal = htmlModal({ layout: { component: (props) => <Default {...props} /> } });
                modal({ url: loginLinkAction.url, onAfterContent: bindComponents });

                GA4Tracking({
                    eventName: 'open_sso_modal',
                    context: 'header',
                    category: 'login'
                });
            }}
        >
            <HeaderItemText>{t(LogIn)}</HeaderItemText>
        </SessionMenuItem>
    );
}

function useLoginUrlTemplate(loginUrlTemplate: string): string {
    const [loginUrl, setLoginUrl] = useState(loginUrlTemplate);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect((): void => {
        const returnUrl = window.encodeURIComponent(window.location.href.substring(window.location.origin.length));

        setLoginUrl(`${loginUrl}&returnurl=${returnUrl}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return loginUrl;
}

function MyProfileNotAuthenticated({
    model: { loginRequest },
    tooltip
}: {
    model: MyProfileViewModel;
    tooltip?: TooltipModelMaybe;
}) {
    const { loginLinkAction } = loginRequest;
    const { t } = useTranslation();
    const loginUrlTemplate = useLoginUrlTemplate(loginLinkAction.url ?? '');
    const isAuthenticated = useIsAuthenticated();
    const [showNudgingStorage, setShowNudgingStorage] = useDynamicStorage(
        'sessionStorage',
        'shouldShowLoginNudgeKey',
        !isAuthenticated
    );

    useEffect(() => {
        if (!isAuthenticated && showNudgingStorage) {
            setShowNudging(true);
        }
    }, [isAuthenticated, showNudgingStorage]);

    const [showNudging, setShowNudging] = useState(false);

    const mutatedLoginLinkAction = { ...loginLinkAction, url: loginUrlTemplate }; // todo niels, what should be happening here?

    return (
        <StyledTooltip
            id={tooltip?.tooltipType}
            enabled={!!tooltip}
            popup
            popupMaxCount={1}
            popupCountStorage="sessionStorage"
            popupDuration={tooltip?.screenTimeLimitSeconds ? tooltip.screenTimeLimitSeconds * 1000 : 15000}
            enableHover={false}
            contentRenderer={{
                headline: tooltip?.title,
                body: tooltip?.content || '',
                action: ({ close }) => (
                    <InlineButton
                        onClick={() => {
                            close();

                            GA4Tracking({
                                eventName: 'click',
                                category: 'navigation',
                                context: 'header',
                                eventParams: [
                                    { param_name: 'click_type', param_value: 'tooltip' },
                                    {
                                        param_name: 'tooltip_type',
                                        param_value: getTooltipType(tooltip?.tooltipType ?? 'ClubMatas')
                                    }
                                ]
                            });
                        }}
                        fontWeight="semibold"
                    >
                        {tooltip?.dismissText}
                    </InlineButton>
                )
            }}
        >
            {(props) => (
                <SessionMenuAnchorItem
                    aria-label={t(LogIn)}
                    icon={
                        showNudging ? (
                            <RelativeContainer>
                                <Icon type="User" size={16} aria-hidden />
                            </RelativeContainer>
                        ) : (
                            <Icon type="User" size={16} aria-hidden />
                        )
                    }
                    action={mutatedLoginLinkAction}
                    onClick={() => {
                        setShowNudgingStorage(false);

                        GA4Tracking({
                            eventName: 'open_sso_modal',
                            context: 'header',
                            category: 'login'
                        });
                    }}
                    {...props}
                >
                    <HeaderItemText>{t(LogIn)}</HeaderItemText>
                </SessionMenuAnchorItem>
            )}
        </StyledTooltip>
    );
}

function MyProfileAuthenticated({
    model: { myProfileLinkAction },
    tooltip
}: {
    model: MyProfileViewModel;
    tooltip?: TooltipModelMaybe;
}) {
    const { t } = useTranslation();

    return (
        <StyledTooltip
            id={tooltip?.tooltipType}
            enabled={!!tooltip}
            popup
            popupDuration={tooltip?.screenTimeLimitSeconds ? tooltip.screenTimeLimitSeconds * 1000 : 15000}
            popupMaxCount={1}
            popupCountStorage="sessionStorage"
            enableHover={false}
            contentRenderer={{
                headline: tooltip?.title,
                body: format(tooltip?.content) || '',
                action: () =>
                    tooltip?.dismissAction ? (
                        <InlineLinkActionButton
                            action={tooltip?.dismissAction}
                            fontWeight="semibold"
                            onClick={() => {
                                GA4Tracking({
                                    eventName: 'click',
                                    category: 'navigation',
                                    context: 'header',
                                    eventParams: [
                                        { param_name: 'click_type', param_value: 'tooltip' },
                                        {
                                            param_name: 'tooltip_type',
                                            param_value: getTooltipType(tooltip?.tooltipType)
                                        }
                                    ]
                                });
                            }}
                        >
                            {tooltip?.dismissText}
                        </InlineLinkActionButton>
                    ) : undefined
            }}
        >
            {(props) => (
                <SessionMenuAnchorItem
                    icon={<Icon type="User" size={16} aria-hidden />}
                    aria-label={t(MyProfileTrans)}
                    action={myProfileLinkAction}
                    onClick={() => {
                        GA4Tracking({
                            eventName: 'go_to_my_matas',
                            context: 'header',
                            category: 'navigation',
                            eventParams: [{ param_name: 'link_type', param_value: 'internal' }]
                        });
                    }}
                    {...props}
                >
                    <HeaderItemText>{t(MyProfileTrans)}</HeaderItemText>
                </SessionMenuAnchorItem>
            )}
        </StyledTooltip>
    );
}

export default function MyProfile({
    model,
    tooltips
}: {
    model: MyProfileViewModel;
    tooltips?: SessionMenuViewModel['tooltips'];
}) {
    const {
        loginRequest: { loginLinkAction }
    } = model;
    const isAuthenticated = useIsAuthenticated();
    const { loginModal } = useLoginLink();

    if (!loginLinkAction.url && loginModal) {
        return <MyProfileLoginModal model={model} />;
    }

    if (!isAuthenticated) {
        return <MyProfileNotAuthenticated model={model} tooltip={tooltips?.clubMatasTooltip} />;
    }

    return <MyProfileAuthenticated model={model} tooltip={tooltips?.memberBenefitsTooltip} />;
}
